import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { rideAPI, driverAPI, notificationAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Car, MapPin, Clock, Bell, User, LogOut, Play, Square, History, Home,
  CheckCircle, XCircle, AlertCircle, Phone, Navigation, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Thirugoo Logo Component
const ThirugooLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="/thirugoo-logo.png" 
    alt="Thirugoo" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('trips');
  const [rides, setRides] = useState([]);
  const [driverProfile, setDriverProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'driver') {
      navigate(user.role === 'admin' ? '/admin' : '/customer');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      // Get driver profile
      const driverRes = await driverAPI.getByUserId(user.id);
      setDriverProfile(driverRes.data);
      setIsAvailable(driverRes.data.is_available);

      // Get rides assigned to this driver
      const ridesRes = await rideAPI.getByDriver(driverRes.data.id);
      setRides(ridesRes.data);

      // Get notifications
      const notifRes = await notificationAPI.getByUser(user.id);
      setNotifications(notifRes.data);

      const unreadRes = await notificationAPI.getUnreadCount(user.id);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 404) {
        toast.info('Driver profile not found. Please contact admin.');
      }
    }
  };

  const handleStartTrip = async (rideId) => {
    setLoading(true);
    try {
      await rideAPI.start(rideId);
      toast.success('Trip started!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    } finally {
      setLoading(false);
    }
  };

  const handleEndTrip = async (rideId) => {
    setLoading(true);
    try {
      await rideAPI.end(rideId);
      toast.success('Trip completed!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end trip');
    } finally {
      setLoading(false);
    }
  };

  const handleAvailabilityToggle = async (value) => {
    if (!driverProfile) return;
    try {
      await driverAPI.update(driverProfile.id, { is_available: value });
      setIsAvailable(value);
      toast.success(value ? 'You are now available for rides' : 'You are now offline');
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await notificationAPI.markAllRead(user.id);
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking notifications read:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      started: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status.toUpperCase()}</Badge>;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const activeRides = rides.filter(r => ['assigned', 'started'].includes(r.status));
  const completedRides = rides.filter(r => r.status === 'completed');

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ThirugooLogo className="h-9 w-9" />
            <span className="text-xl font-bold text-gray-900">Thirugoo Driver</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={`text-sm ${isAvailable ? 'text-green-600' : 'text-gray-500'}`}>
                {isAvailable ? 'Online' : 'Offline'}
              </span>
              <Switch 
                checked={isAvailable} 
                onCheckedChange={handleAvailabilityToggle}
              />
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setActiveTab('notifications')}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Welcome, {user.name}!</h1>
          <p className="text-gray-600">
            {activeRides.length > 0 
              ? `You have ${activeRides.length} active ride(s)` 
              : 'No active rides. Stay online for new assignments!'}
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{activeRides.length}</p>
              <p className="text-xs text-blue-600">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{completedRides.length}</p>
              <p className="text-xs text-green-600">Completed</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{driverProfile?.rating?.toFixed(1) || '5.0'}</p>
              <p className="text-xs text-amber-600">Rating</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="trips" className="flex items-center gap-2">
              <Navigation className="h-4 w-4" />
              <span className="hidden sm:inline">Trips</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2 relative">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Alerts</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
          </TabsList>

          {/* Active Trips Tab */}
          <TabsContent value="trips">
            <div className="space-y-4">
              {activeRides.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Navigation className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No active trips</p>
                    <p className="text-sm text-gray-400">Stay online to receive new ride assignments</p>
                  </CardContent>
                </Card>
              ) : (
                activeRides.map((ride) => (
                  <Card key={ride.id} className="overflow-hidden border-2 border-amber-200">
                    <CardHeader className="bg-amber-50 py-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Active Trip</CardTitle>
                        {getStatusBadge(ride.status)}
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm font-medium">Customer: {ride.customer_name}</p>
                          <p className="text-sm text-gray-600 flex items-center gap-1">
                            <Phone className="h-3 w-3" /> {ride.customer_phone}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500">PICKUP</p>
                              <p className="text-sm font-medium">{ride.pickup_location}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 text-red-500 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-gray-500">DROP</p>
                              <p className="text-sm font-medium">{ride.drop_location}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                          <div>
                            <p className="text-xs text-gray-500">Scheduled</p>
                            <p className="text-sm">{format(new Date(ride.scheduled_time), 'PPp')}</p>
                          </div>
                          <p className="text-xl font-bold text-amber-600">
                            Rs. {ride.fare_estimate?.toFixed(0)}
                          </p>
                        </div>

                        {ride.notes && (
                          <div className="bg-blue-50 p-2 rounded text-sm text-blue-800">
                            Note: {ride.notes}
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          {ride.status === 'assigned' && (
                            <Button 
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              onClick={() => handleStartTrip(ride.id)}
                              disabled={loading}
                            >
                              {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <><Play className="h-4 w-4 mr-2" /> Start Trip</>
                              )}
                            </Button>
                          )}
                          {ride.status === 'started' && (
                            <Button 
                              className="flex-1 bg-red-600 hover:bg-red-700"
                              onClick={() => handleEndTrip(ride.id)}
                              disabled={loading}
                            >
                              {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <><Square className="h-4 w-4 mr-2" /> End Trip</>
                              )}
                            </Button>
                          )}
                          <Button variant="outline" size="icon" asChild>
                            <a href={`tel:${ride.customer_phone}`}>
                              <Phone className="h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="space-y-4">
              {completedRides.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No completed trips yet</p>
                  </CardContent>
                </Card>
              ) : (
                completedRides.map((ride) => (
                  <Card key={ride.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm text-gray-500">
                            {format(new Date(ride.scheduled_time), 'PPp')}
                          </p>
                          {getStatusBadge(ride.status)}
                        </div>
                        <p className="text-lg font-bold text-green-600">
                          Rs. {ride.fare_estimate?.toFixed(0)}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm"><span className="text-gray-500">From:</span> {ride.pickup_location}</p>
                        <p className="text-sm"><span className="text-gray-500">To:</span> {ride.drop_location}</p>
                        <p className="text-sm"><span className="text-gray-500">Customer:</span> {ride.customer_name}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-amber-600" />
                  Notifications
                </CardTitle>
                {unreadCount > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllNotificationsRead}>
                    Mark all read
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="py-8 text-center">
                    <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No notifications yet</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {notifications.map((notif) => (
                        <div 
                          key={notif.id} 
                          className={`p-3 rounded-lg border ${!notif.is_read ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}
                        >
                          <div className="flex items-start gap-3">
                            {notif.notification_type === 'success' ? (
                              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                            ) : notif.notification_type === 'warning' ? (
                              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                            ) : (
                              <Bell className="h-5 w-5 text-blue-500 mt-0.5" />
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm">{notif.title}</p>
                              <p className="text-sm text-gray-600">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {format(new Date(notif.created_at), 'PPp')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-amber-600" />
                  Driver Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center">
                    <User className="h-8 w-8 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{user.name}</p>
                    <p className="text-gray-600 flex items-center gap-1">
                      <Phone className="h-4 w-4" /> +91 {user.phone}
                    </p>
                    <Badge className={isAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {isAvailable ? 'Online' : 'Offline'}
                    </Badge>
                  </div>
                </div>

                {driverProfile && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{driverProfile.total_trips}</p>
                        <p className="text-sm text-gray-600">Total Trips</p>
                      </div>
                      <div className="bg-amber-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-amber-600">{driverProfile.experience_years} yrs</p>
                        <p className="text-sm text-gray-600">Experience</p>
                      </div>
                    </div>

                    {driverProfile.vehicle_info && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-gray-500 mb-1">Assigned Vehicle</p>
                        <p className="font-medium">{driverProfile.vehicle_info.make} {driverProfile.vehicle_info.model}</p>
                        <p className="text-sm text-gray-600">{driverProfile.vehicle_info.number_plate}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500">Music Preference</p>
                        <p className="font-medium">{driverProfile.music_preference}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-500">Food Offering</p>
                        <p className="font-medium">{driverProfile.food_offering}</p>
                      </div>
                    </div>
                  </div>
                )}

                <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                  <Home className="mr-2 h-4 w-4" /> Back to Home
                </Button>
                <Button variant="destructive" className="w-full" onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default DriverDashboard;
